import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { DealProps, ModalType } from 'states/initialState';
import { Button, Stack } from 'react-bootstrap';
import { DecimalFormat, IntegerFormat, MoneyFormat } from '../Common/TextFormat';
import { useTracking } from 'react-tracking';

interface SelectedDealProps {
  onlineCheckoutEligible: boolean;
  maxLoanAmount: number;
  deal: DealProps;
  setModalType: any;
  disableCheckout: boolean;
  handleShow: any;
  fixedCommission: boolean;
}

const SelectedDeal = ({
  onlineCheckoutEligible,
  maxLoanAmount,
  deal,
  setModalType,
  disableCheckout,
  handleShow,
  fixedCommission
}: SelectedDealProps) => {
  const intl = useIntl();

  const { trackEvent } = useTracking();

  const handleClick = () => {
    setModalType(ModalType.TL);
    handleShow();
    trackEvent({
      event: 'Send Checkout',
      loanAmount: deal.loanAmount,
      paymentFrequency: deal.paymentFrequency,
      term: deal.term,
      commission: deal.commission,
      onlineOpportunityEligible: onlineCheckoutEligible,
      product: ModalType.TL,
      maxLoanAmount: maxLoanAmount
    });
  };

  return (
    <div className="h-100 d-grid" style={{ gridTemplateRows: '40px' }}>
      <svg
        className="position-absolute text-secondary"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 1080 1080"
      >
        <g transform="matrix(0 14 -14 0 450 540)">
          <polygon points="-37.43,32.41 0,-32.41 37.43,32.41 " />
        </g>
      </svg>
      <h2 className="text-center fw-400">
        <FormattedMessage id="selectedDeal.header" />
      </h2>
      <Stack gap={3} className="mx-4 pb-3">
        <Stack gap={2} className="fs-5 p-3 bg-light">
          <Stack direction="horizontal">
            <FormattedMessage id="selectedDeal.loanAmt" />
            <MoneyFormat value={deal.loanAmount} cents={false} className="ms-auto"/>
          </Stack>
          <Stack direction="horizontal">
            <FormattedMessage id="selectedDeal.origFee" />
            <MoneyFormat value={deal.originationFee} className="ms-auto"/>
          </Stack>
          <Stack direction="horizontal">
            <FormattedMessage id="selectedDeal.disbursementAmt" />
            <MoneyFormat value={deal.disbursementAmount} className="ms-auto"/>
          </Stack>
          <Stack direction="horizontal">
            <FormattedMessage id="selectedDeal.payback" />
            <MoneyFormat value={deal.totalAmountPaidBack} className="ms-auto"/>
          </Stack>
        </Stack>
        <Stack gap={2} className="fs-5 px-3">
          <Stack direction="horizontal">
            <FormattedMessage id="selectedDeal.term" />
            <IntegerFormat value={deal.term} className="ms-auto"
              suffix={intl.formatMessage({ id: 'selectedDeal.months' })}
            />
          </Stack>
          <Stack direction="horizontal">
            <FormattedMessage id="selectedDeal.paymentFreq" />
            <span className="ms-auto text-secondary text-capitalize">
              {deal.paymentFrequency?.toLowerCase()}
            </span>
          </Stack>
          <Stack direction="horizontal">
            <FormattedMessage id="selectedDeal.apr" />
            <DecimalFormat value={deal.apr} className="ms-auto" suffix="%"/>
          </Stack>
          <Stack direction="horizontal">
            <FormattedMessage id="selectedDeal.cod" />
            <DecimalFormat value={deal.centsOnDollar} decimalScale={3} className="ms-auto"/>
          </Stack>
          <Stack direction="horizontal">
            <FormattedMessage id="selectedDeal.payAmt" />
            <MoneyFormat value={deal.payment} className="ms-auto" />
          </Stack>
          <Stack direction="horizontal">
            <FormattedMessage id="selectedDeal.numPaymts" />
            <IntegerFormat value={deal.numberOfPayments} className="ms-auto"/>
          </Stack>
        </Stack>
      </Stack>
      <div className="w-100 px-4" style={{ alignSelf: 'flex-end' }}>
        <hr />
        <Stack className="px-3 pb-4" direction="horizontal">
          <FormattedMessage id="selectedDeal.commission" />:
          { fixedCommission ? <span className="ms-auto text-secondary">---</span> : 
          <MoneyFormat value={deal.commission} className="ms-auto"/>
        }
        </Stack>
        <Button
          disabled={disableCheckout}
          className="w-100"
          onClick={handleClick}
          data-testid="selectedDealCheckout"
        >
          <FormattedMessage id="selectedDeal.checkout" />
        </Button>
      </div>
    </div>
  );
};

export default SelectedDeal;
